import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Landmark, Plus, Loader2, Trash2, FileDown, Building2 } from "lucide-react";
import HypothesesForm from "../components/previsionnel/HypothesesForm";
import ResultatsView from "../components/previsionnel/ResultatsView";
import CompareView from "../components/previsionnel/CompareView";

export default function PrevisionnelPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const { currentSociete } = useSociete();
  const sid = currentSociete?.societe_id;

  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [compare, setCompare] = useState(null);

  const loadDetail = useCallback(async (pid) => {
    try {
      const { data } = await api.get(`/previsionnel/${pid}`);
      setDetail(data);
      setCompare(null);
    } catch { toast.error("Prévisionnel introuvable"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadList = useCallback(async () => {
    if (!sid) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/previsionnel?societe_id=${sid}`);
      const rows = data.previsionnels || [];
      setList(rows);
      if (rows.length > 0) await loadDetail(rows[0].previsionnel_id);
      else setDetail(null);
    } catch { setList([]); }
    setLoading(false);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadList(); }, [loadList]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/previsionnel", { societe_id: sid, name: newName || "Prévisionnel banque" });
      setNewName("");
      setList(l => [{ previsionnel_id: data.previsionnel_id, name: data.name, created_at: data.created_at }, ...l]);
      setDetail(data);
      toast.success("Prévisionnel créé — hypothèses pré-remplies depuis vos données réelles");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de création"); }
    finally { setCreating(false); }
  };

  const remove = async (pid) => {
    if (!window.confirm("Supprimer ce prévisionnel ?")) return;
    await api.delete(`/previsionnel/${pid}`);
    toast.success("Supprimé");
    loadList();
  };

  const saveHypotheses = async (hypotheses, name) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/previsionnel/${detail.previsionnel_id}`, { hypotheses, name });
      setDetail(data);
      setList(l => l.map(p => p.previsionnel_id === data.previsionnel_id ? { ...p, name: data.name } : p));
      toast.success("Hypothèses enregistrées — projections recalculées");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/previsionnel/${detail.previsionnel_id}/analyze`);
      setDetail(d => ({ ...d, ai_note: data.ai_note }));
      toast.success("Note de synthèse générée");
    } catch (e) { toast.error(e.response?.data?.detail || "Analyse IA en échec"); }
    finally { setAnalyzing(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.get(`/previsionnel/${detail.previsionnel_id}/export.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Previsionnel_${(currentSociete?.name || "").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible"); }
    finally { setExporting(false); }
  };

  if (!sid) return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="previsionnel-page">
      <PageHeader icon={<Landmark />} title="Prévisionnel banque" description="Dossier prévisionnel 3 ans construit à partir de vos données réelles" testId="previsionnel-header" />
      <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl">
        <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <p className="text-sm text-slate-400">Sélectionnez une société dans le menu latéral pour construire son prévisionnel.</p>
      </div>
    </div>
  );

  const socModuleOff = currentSociete?.enabled_modules && currentSociete.enabled_modules.gestion_activite === false;
  if (socModuleOff) return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="previsionnel-page">
      <PageHeader icon={<Landmark />} title="Prévisionnel banque" description="Dossier prévisionnel 3 ans construit à partir de vos données réelles" testId="previsionnel-header" />
      <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl" data-testid="previsionnel-module-off">
        <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-600" />
        <p className="text-sm text-slate-400 font-medium">Le module « Gestion d'activité » n'est pas activé pour {currentSociete?.name}.</p>
        <p className="text-[11px] text-slate-500 mt-1">Activez-le dans Sociétés → Modifier → Modules, ou basculez sur une autre société.</p>
      </div>
    </div>
  );

  return (
    <div className="p-4 md:p-6 max-w-[1500px] mx-auto" data-testid="previsionnel-page">
      <PageHeader icon={<Landmark />} title="Prévisionnel banque"
        description="Compte de résultat, trésorerie et plan de financement sur 3 ans — prêt à présenter à votre banque"
        testId="previsionnel-header" />

      {/* Barre scénarios */}
      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="previsionnel-list-bar">
        {list.map(p => (
          <button key={p.previsionnel_id}
            onClick={() => loadDetail(p.previsionnel_id)}
            className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${detail?.previsionnel_id === p.previsionnel_id ? "bg-[#f7c935] text-[#0b0811] border-[#f7c935]" : "bg-[#14101c] text-slate-300 border-white/10 hover:border-white/25"}`}
            data-testid={`previsionnel-pill-${p.previsionnel_id}`}>
            {p.name}
            <Trash2 data-testid={`previsionnel-delete-${p.previsionnel_id}`}
              className={`w-3 h-3 opacity-0 group-hover:opacity-100 ${detail?.previsionnel_id === p.previsionnel_id ? "text-[#0b0811]" : "text-rose-400"}`}
              onClick={(e) => { e.stopPropagation(); remove(p.previsionnel_id); }} />
          </button>
        ))}
        <div className="flex items-center gap-1.5">
          <Input className="bg-[#0b0811] border-white/10 text-white h-8 w-48 text-xs" placeholder="Nom du scénario (ex : Prudent)"
            value={newName} onChange={e => setNewName(e.target.value)} data-testid="previsionnel-new-name" />
          <Button size="sm" onClick={create} disabled={creating} className="bg-[#f7c935] text-[#0b0811] font-bold h-8" data-testid="previsionnel-create-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Nouveau</>}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>
      ) : !detail ? (
        <div className="text-center py-20 bg-[#14101c] border border-dashed border-white/10 rounded-xl" data-testid="previsionnel-empty">
          <Landmark className="w-12 h-12 mx-auto mb-3 text-slate-600" />
          <p className="text-slate-400 text-sm font-medium">Aucun prévisionnel pour {currentSociete?.name}</p>
          <p className="text-[11px] text-slate-500 mt-1">Créez un scénario : les hypothèses seront pré-remplies avec le CA, les charges et la trésorerie réels de la société.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="text-xs text-slate-500">Scénario actif : <strong className="text-white">{detail.name}</strong></span>
            <div className="ml-auto flex gap-2 items-center">
              {list.length > 1 && (
                <select value="" onChange={async e => {
                    if (!e.target.value) return;
                    try {
                      const { data } = await api.get(`/previsionnel/${e.target.value}`);
                      setCompare(data);
                    } catch { toast.error("Scénario introuvable"); }
                  }}
                  className="bg-[#0b0811] border border-white/10 rounded-lg text-xs text-slate-300 px-2 h-8"
                  data-testid="compare-select">
                  <option value="">⚖ Comparer avec…</option>
                  {list.filter(p => p.previsionnel_id !== detail.previsionnel_id).map(p => (
                    <option key={p.previsionnel_id} value={p.previsionnel_id}>{p.name}</option>
                  ))}
                </select>
              )}
              <Button size="sm" variant="outline" onClick={exportPdf} disabled={exporting}
                className="border-[#f7c935]/40 text-[#f7c935] hover:bg-[#f7c935]/10 text-xs" data-testid="previsionnel-export-pdf-btn">
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FileDown className="w-3.5 h-3.5 mr-1" /> Exporter le dossier PDF</>}
              </Button>
            </div>
          </div>
          {compare ? (
            <CompareView a={detail} b={compare} onClose={() => setCompare(null)} />
          ) : (
            <div className="grid lg:grid-cols-[380px_1fr] gap-5 items-start">
              <HypothesesForm key={detail.previsionnel_id} api={api} societeId={sid}
                initialHyp={detail.hypotheses} initialName={detail.name}
                onSave={saveHypotheses} saving={saving} />
              <ResultatsView computed={detail.computed} aiNote={detail.ai_note}
                onAnalyze={analyze} analyzing={analyzing} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
